import Backbone from "backbone";
import * as CryptoJS from "CryptoJS_MD5";
import * as _ from "underscore";

import * as featureFlags from "featureFlags";
import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";
import * as utils from "shared/utils";
import * as textToSpeech from "textToSpeech";

export default class UserModel extends Backbone.Model<User> {
  url = () => "/api/user";

  defaults() {
    return {
      username: "Guest",
      currentLearningLanguage: "es",
      firstLanguage: "en",
      books: {},
      updateHour: 0, // when new words are received
      sortBooksBy: "readDate",
      publicBooksCategories: ["All"],
      publicBooksFilter: {
        includeAI: true,
        metrics_CEF: "A1-C2",
        metrics_words: "0-Infinity",
      },
      publicSortBooksBy: "hotness",
      dailyPracticeTarget: 10,
      customDictionaries: {},

      readerAlign: "alignLeft",
      readerDeselectBehavior: "delete",
      readerWordHighlights: "off",
      readerFont: "Jost",
      readerTranslations: "on",
      readerSentenceBreaks: "off",
      readerTranslationStyle: "glossNew",
      readerMergePhrases: "on",
      readerStreakShow: "always",

      practiceLength: "small",
      smartDictionaryInEnglish: {},
      reviewSummaryInEnglish: {},
      soundEffectsEnabled: true,
      speakingMode: false,
      featureFlags: {},
    };
  }

  idAttribute = "_id";

  constructor(attributes?: any, options?: Backbone.ModelSetOptions) {
    super(attributes, options);
  }

  initialize() {
    this.on("change:email", () => {
      this.set("emailMD5", CryptoJS.MD5(this.get("email")).toString());
    });
  }

  viewJSON() {
    const user = this.toJSON();

    user.firstLanguageString = languageMap.map[user.firstLanguage].name;
    user.firstLanguageEnglishString =
      languageMap.map[user.firstLanguage].english;
    user.currentLearningLanguageString =
      languageMap.map[user.currentLearningLanguage].name;
    user.currentLearningLanguageEnglishString =
      languageMap.map[user.currentLearningLanguage].english;

    if (utils.childExists(user, "googlePlusInfo.image.url")) {
      user.imageURL = user.googlePlusInfo.image.url;
    } else if (utils.childExists(user, "googlePlusInfo.picture")) {
      user.imageURL = user.googlePlusInfo.picture;
    } else {
      user.imageURL = "//gravatar.com/avatar/" + user.emailMD5 + "?d=mm&s=72";
    }
    if (this.isGod()) {
      user.godMode = true;
    }

    return user;
  }

  isGod() {
    // return false;
    return this.id === "50cb58dbf939c7397b000002";
  }

  isIeva() {
    return this.id === "65d480a9ddac06608957a700";
  }

  getFlashcardMode() {
    if (
      _.contains(
        this.get("passiveModeLanguages") || [],
        this.get("currentLearningLanguage")
      )
    ) {
      return "passive";
    }
    if (
      _.contains(
        this.get("activeModeLanguages") || [],
        this.get("currentLearningLanguage")
      )
    ) {
      return "active";
    }
    return "mixed";
  }

  setFlashcardMode(mode) {
    let passiveModeLanguages;
    let activeModeLanguages;

    if (mode === "passive") {
      passiveModeLanguages = _.union(this.get("passiveModeLanguages") || [], [
        this.get("currentLearningLanguage"),
      ]);
      activeModeLanguages = _.difference(
        this.get("activeModeLanguages") || [],
        [this.get("currentLearningLanguage")]
      );
    } else if (mode === "active") {
      passiveModeLanguages = _.difference(
        this.get("passiveModeLanguages") || [],
        [this.get("currentLearningLanguage")]
      );
      activeModeLanguages = _.union(this.get("activeModeLanguages") || [], [
        this.get("currentLearningLanguage"),
      ]);
    } else {
      passiveModeLanguages = _.difference(
        this.get("passiveModeLanguages") || [],
        [this.get("currentLearningLanguage")]
      );
      activeModeLanguages = _.difference(
        this.get("activeModeLanguages") || [],
        [this.get("currentLearningLanguage")]
      );
    }

    this.save({ passiveModeLanguages, activeModeLanguages }, { patch: true });
  }

  hasPremiumAccess() {
    return _.contains(
      ["standard", "plus", "freeForever"],
      this.get("subscriptionPlan")
    );
  }

  hasPremiumPlusAccess() {
    return _.contains(["plus", "freeForever"], this.get("subscriptionPlan"));
  }

  /**
   * @param {string} language - language code
   *
   * @returns {boolean} - true if the user has enabled the smart dictionary in English for the given
   *                      learning language. Defaults to true if they haven't set it.
   */
  isSmartDictionaryInFirstLanguage(language) {
    const smartDictionaryInEnglish = this.get("smartDictionaryInEnglish");
    return smartDictionaryInEnglish[language] ?? true;
  }

  /**
   * @param {string} language - language code
   * @param {boolean} inEnglish
   */
  setSmartDictionaryInFirstLanguage(language, inEnglish) {
    const smartDictionaryInEnglish = this.get("smartDictionaryInEnglish");
    smartDictionaryInEnglish[language] = inEnglish;
    this.save({ smartDictionaryInEnglish }, { patch: true });
  }

  /**
   * @param {string} language - language code
   *
   * @returns {boolean} - true if the user has enabled the review summary in English for the given
   *                      learning language. Defaults to true if they haven't set it.
   */
  isReviewSummaryInEnglish(language) {
    const reviewSummaryInEnglish = this.get("reviewSummaryInEnglish");
    return reviewSummaryInEnglish[language] ?? true;
  }

  /**
   * @param {string} language - language code
   * @param {boolean} inEnglish
   */
  setReviewSummaryToEnglish(language, inEnglish) {
    let reviewSummaryInEnglish = this.get("reviewSummaryInEnglish");
    reviewSummaryInEnglish = {
      ...reviewSummaryInEnglish,
      [language]: inEnglish,
    };
    this.save({ reviewSummaryInEnglish }, { patch: true });
  }

  /**
   * This will update the user's reading speed based on new data. This is dampened to avoid
   * large fluctuations.
   * Consider improving this by discarding outliers.
   */
  updateReadingSpeed(language, newReadingSpeed) {
    const dampingFactor = 0.8;
    const maxChange = 15;

    const delta = Math.max(
      -maxChange,
      Math.min(
        maxChange,
        (newReadingSpeed - this.getReadingSpeed(language)) * (1 - dampingFactor)
      )
    );

    const readingSpeeds = this.get("readingSpeeds") || {};
    readingSpeeds[language] = this.getReadingSpeed(language) + delta;

    this.save({ readingSpeeds }, { patch: true });
  }

  getReadingSpeed(language) {
    const defaultSpeed = 180;

    return Math.round(
      (this.get("readingSpeeds") ?? {})[language] ?? defaultSpeed
    );
  }

  async hasNewStreakFeature() {
    return !(await readlangClient.isLocalMode());
  }

  activeAndCurrentLanguages(): string[] {
    return _.union(this.get("activeLanguages"), [
      this.get("currentLearningLanguage"),
    ]);
  }

  hasFeature(featureKey: featureFlags.UserFlagKey): boolean {
    const spec = featureFlags.userFlags.find(
      (userFlag) => userFlag.key === featureKey
    );

    if (
      this.get("subscriptionPlan") !== "plus" &&
      this.get("subscriptionPlan") !== "freeForever" &&
      spec.availableTo === "plus"
    ) {
      return false;
    }

    if (!this.hasPremiumAccess() && spec.availableTo === "premium") {
      return false;
    }

    return this.get("featureFlags")[featureKey] ?? spec.defaultValue ?? false;
  }

  setFeature(featureKey: featureFlags.UserFlagKey, value: boolean) {
    const featureFlagsMap = this.get("featureFlags");
    featureFlagsMap[featureKey] = value;
    this.save({ featureFlags: featureFlagsMap }, { patch: true });
  }

  hasSpeakingModeAccess(language: string): boolean {
    const result =
      textToSpeech.languageSupported(language) && this.hasPremiumAccess();

    return result;
  }

  isLocalMode() {
    return this.id === "local";
  }

  speakingModeEnabled(language: string): boolean {
    return this.hasSpeakingModeAccess(language) && this.get("speakingMode");
  }

  hasChatFeature() {
    return (
      this.hasPremiumAccess() &&
      languageMap.languageSupportsChat(this.get("currentLearningLanguage"))
    );
  }
}
